import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import api from "../../lib/api";
import { BookOpen } from "lucide-react";

/**
 * "Knowledge" header link with a small live progress chip — fetches once on
 * mount so the count is accurate; refreshes on tab focus.
 */
export default function HeaderKnowledgeLink() {
  const [status, setStatus] = useState({ answered: 0, total: 0, loaded: false });

  const fetchStatus = () => {
    Promise.all([
      api.get("/knowledge/questions"),
      api.get("/knowledge/answers"),
    ])
      .then(([q, a]) => {
        const total = q.data.length;
        const answered = q.data.reduce(
          (n, x) => n + ((a.data?.[x.id] || "").trim() ? 1 : 0),
          0,
        );
        setStatus({ answered, total, loaded: true });
      })
      .catch(() => setStatus({ answered: 0, total: 0, loaded: true }));
  };

  useEffect(() => {
    fetchStatus();
    const onFocus = () => fetchStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const incomplete = status.loaded && status.total > 0 && status.answered < status.total;

  return (
    <NavLink
      to="/knowledge"
      data-testid="nav-knowledge"
      className={({ isActive }) =>
        `relative px-3 py-2 text-sm tracking-tight transition-colors inline-flex items-center gap-2 rounded-lg border ${
          isActive
            ? "text-emerald-200 border-emerald-500/40 bg-emerald-500/10"
            : incomplete
            ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/[0.06] hover:bg-emerald-500/10"
            : "text-zinc-400 border-transparent hover:text-white"
        }`
      }
    >
      <BookOpen className="w-3.5 h-3.5" />
      Knowledge
      {status.loaded && status.total > 0 && (
        <span
          data-testid="nav-knowledge-badge"
          className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
            incomplete
              ? "bg-amber-500/15 text-amber-200 border border-amber-500/30"
              : "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"
          }`}
        >
          {status.answered}/{status.total}
        </span>
      )}
      {incomplete && (
        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
      )}
    </NavLink>
  );
}
